import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { getVisitorGeo } from "@/lib/visitor-country.functions";
import { countryToJurisdiction, type Jurisdiction } from "@/lib/jurisdiction";

const COOKIE_KEY = "symmetria.country";

function readCookieCountry(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(/(?:^|; )symmetria\.country=([^;]+)/);
  return m ? decodeURIComponent(m[1]).toUpperCase().slice(0, 2) : null;
}

function writeCookieCountry(country: string) {
  if (typeof document === "undefined") return;
  const exp = new Date(Date.now() + 365 * 24 * 3600 * 1000).toUTCString();
  document.cookie = `${COOKIE_KEY}=${encodeURIComponent(country)}; expires=${exp}; path=/; SameSite=Lax`;
}

export function useJurisdiction() {
  const fetchGeo = useServerFn(getVisitorGeo);
  const [country, setCountry] = useState<string | null>(null);
  const [jurisdiction, setJurisdiction] = useState<Jurisdiction>("DEFAULT");
  const [resolved, setResolved] = useState(false);

  useEffect(() => {
    const cookie = readCookieCountry();
    if (cookie) {
      setCountry(cookie);
      setJurisdiction(countryToJurisdiction(cookie));
      setResolved(true);
      return;
    }
    let cancelled = false;
    fetchGeo()
      .then((g) => {
        if (cancelled) return;
        setCountry(g.country);
        setJurisdiction(g.jurisdiction);
      })
      .catch(() => { /* keep default */ })
      .finally(() => { if (!cancelled) setResolved(true); });
    return () => { cancelled = true; };
  }, [fetchGeo]);

  function setManualCountry(code: string) {
    const c = code.toUpperCase().slice(0, 2);
    writeCookieCountry(c);
    setCountry(c);
    setJurisdiction(countryToJurisdiction(c));
  }

  return { country, jurisdiction, resolved, setManualCountry };
}
